import { faCheckCircle, faCircleNotch, faInfoCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import loadable from "@loadable/component";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { useCallback, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";

import IconWA from "src/assets/images/common/icon-whatsapp.png";

import Form from "src/components-v2/Form";
import Icon from "src/components-v2/Icon";

import { ModalStyled, ModalTitle, OTPWrapper } from "./index.styled";
import { Link } from "react-router-dom";
import privateRoutes from "src/routes/private-routes";
import Alert from "src/components-v2/Alert";
import { useOTPModal } from "./useOTPModal";

const CountdownWrapper = loadable(() => import("src/components/CountdownWrapper"));

export type OTPInputModalProps = {
  show: boolean;
  onCloseModal: () => void;
  handleSendOTPWithOtherChannel: () => void;
  handleSendOTPWithOtherPhone: () => void;
  handleResendOTP: () => void;
  isOTPViaSMS: boolean;
  isOTPCompanyPhoneNumber: boolean;
  phoneNumber: string;
  hasPhoneNumber: boolean;
  countdownKey: string;
  countdownValue: number;
  otherValues: Record<string, string>;
  title: string;
  isShowAlertActivateGAuth: boolean;
  onSubmit?: (otpCode: string) => Promise<void>;
  startCountdown?: boolean;
  onCountdownFinish?: () => void;
  showSMSFallback?: boolean;
};

const EXCEEDED_OTP_LIMIT_ERROR_CODE = 1106;

const defaultProps = {
  show: false,
  onCloseModal: () => {},
  handleSendOTPWithOtherChannel: () => {},
  handleSendOTPWithOtherPhone: () => {},
  handleResendOTP: () => {},
  isOTPViaSMS: false,
  isOTPCompanyPhoneNumber: false,
  phoneNumber: "",
  hasPhoneNumber: true,
  countdownKey: "",
  countdownValue: 0,
  otherValues: {},
  title: "",
  isShowAlertActivateGAuth: false,
  onSubmit: () => {},
  startCountdown: false,
  onCountdownFinish: () => {},
  showSMSFallback: false
};

function OTPInputModal(props: OTPInputModalProps) {
  const {
    show,
    onCloseModal,
    countdownKey,
    countdownValue,
    otherValues,
    title,
    isOTPViaSMS,
    isOTPCompanyPhoneNumber,
    phoneNumber,
    hasPhoneNumber,
    handleResendOTP,
    handleSendOTPWithOtherChannel,
    handleSendOTPWithOtherPhone,
    isShowAlertActivateGAuth,
    onSubmit,
    startCountdown,
    onCountdownFinish,
    showSMSFallback
  } = props;

  // Use internal OTP state management
  const {
    state: {
      otpCode,
      isSubmitting,
      otpApiErrors,
      otpApiErrorCode,
      statusOtp,
      isCountingDown,
      startCountdown: internalStartCountdown
    },
    handleOtpChange,
    handleCountdownStart,
    handleCountdownFinish: internalHandleCountdownFinish,
    setState
  } = useOTPModal();

  const { t } = useTranslation(["translation", "errors"]);

  // Remove isIndonesianPhoneNumber, use showSMSFallback instead

  // Sync external startCountdown prop with internal state
  useEffect(() => {
    if (startCountdown && !internalStartCountdown) {
      setState({ startCountdown: true });
    } else if (!startCountdown && internalStartCountdown) {
      setState({ startCountdown: false });
    }
    // Don't override internal state when external prop is true but countdown has finished
    // This prevents infinite loop when countdown finishes but external prop remains true
  }, [startCountdown, internalStartCountdown, setState]);

  // Custom countdown finish handler that notifies parent
  const handleCountdownFinish = useCallback(() => {
    internalHandleCountdownFinish();
    onCountdownFinish?.();
  }, [internalHandleCountdownFinish, onCountdownFinish]);

  // Debounced submission handler
  const debouncedOnSubmit = useDebouncedCallback(async (otp: string) => {
    if (onSubmit && !isSubmitting) {
      setState({ isSubmitting: true });

      try {
        await onSubmit(otp);
        setState({ statusOtp: "success" });
      } catch (error) {
        // Handle different error object structures
        let errorMessages = [];
        let errorCode = 0;

        if (error && typeof error === "object") {
          // Handle error object with messages array
          if (Array.isArray(error.messages)) {
            errorMessages = error.messages;
          }
          // Handle error object with message string
          else if (error.message) {
            errorMessages = [error.message];
          }
          // Handle error object with single string
          else if (typeof error === "string") {
            errorMessages = [error];
          }
          // Handle error code
          if (error.code) {
            errorCode = error.code;
          }
        }
        // Handle string errors
        else if (typeof error === "string") {
          errorMessages = [error];
        }
        // Fallback
        else {
          errorMessages = ["An error occurred during OTP submission"];
        }

        setState({
          statusOtp: "error",
          otpApiErrors: errorMessages,
          otpApiErrorCode: errorCode
        });
      } finally {
        setState({ isSubmitting: false });
      }
    }
  }, 1500);

  // Handle OTP change with debounced submission
  const handleOtpChangeWithDebounce = useCallback(
    (otp: string) => {
      handleOtpChange(otp);

      // Clear errors when user starts typing
      if (otpApiErrors.length > 0) {
        setState({
          otpApiErrors: [],
          otpApiErrorCode: 0,
          statusOtp: ""
        });
      }

      // Submit when OTP is complete
      if (otp.length === 6) {
        debouncedOnSubmit(otp);
      }
    },
    [handleOtpChange, otpApiErrors.length, setState, debouncedOnSubmit]
  );

  // Reset internal state when modal closes
  const handleCloseModal = () => {
    // Don't reset countdown state when closing modal
    setState({
      otpCode: "",
      isSubmitting: false,
      otpApiErrors: [],
      otpApiErrorCode: 0,
      statusOtp: "",
      isCountingDown: false
      // Keep startCountdown state intact
    });
    onCloseModal();
  };

  return (
    <ModalStyled closeIcon onClose={handleCloseModal} show={show} withoutHeader>
      <div>
        <div className="u-w-full u-px-4 u-pt-4 u-text-right">
          <span
            onClick={handleCloseModal}
            className="u-text-neutral u-text-xl u-p-2 u-cursor-pointer hover:u-text-dark-smoke"
          >
            &#10005;
          </span>
        </div>
        <div className="u-w-full u-text-center">
          <ModalTitle>
            {!isOTPViaSMS ? <img src={IconWA} alt="whatsapp" className="u-mr-2 u-h-6" /> : null}
            {title}
          </ModalTitle>
        </div>
        <Form>
          <div className="u-p-8 u-text-center">
            <p className="u-mb-2">{t("otp-input.description")}</p>
            <p className="u-mb-6">
              <Trans>
                {t("otp-input.destination", {
                  otpType: phoneNumber,
                  channel: isOTPViaSMS ? "SMS" : "WhatsApp"
                })}
              </Trans>
            </p>
            <OTPWrapper className="u-relative">
              <div className="u-inline-block">
                <OtpInput
                  value={otpCode}
                  numInputs={6}
                  inputStyle={classNames("c-otp-input-box", !!otpApiErrors?.length && "c-otp-input-box--error")}
                  disabledStyle="c-otp-input-box--disabled"
                  shouldAutoFocus
                  isInputNum
                  //   renderInput={(props) => <input {...props} />}
                  isDisabled={isSubmitting}
                  onChange={handleOtpChangeWithDebounce}
                />
              </div>
              {isSubmitting && (
                <div className="u-relative u-inline-block">
                  <Icon icon={faCircleNotch} color="orange" className="u-animate-spin" />
                </div>
              )}
              {statusOtp !== "" && otpCode.length === 6 && (
                <Icon
                  icon={statusOtp === "success" ? faCheckCircle : faTimesCircle}
                  className={classNames(
                    "u-text-large",
                    statusOtp === "success" ? "u-text-jade" : "u-text-ketchup-tomato"
                  )}
                />
              )}
            </OTPWrapper>
            {!!otpApiErrors?.length ? (
              <p className="u-my-2 u-text-ketchup-tomato">
                {otpApiErrorCode === EXCEEDED_OTP_LIMIT_ERROR_CODE
                  ? t(`errors:${otpApiErrorCode}-rate`)
                  : otpApiErrors.join(", ")}
              </p>
            ) : null}

            <div className="u-m-0 u-mt-6">
              {internalStartCountdown ? (
                <CountdownWrapper
                  countdownKey={countdownKey}
                  countdownValue={countdownValue}
                  i18nKey={`otp-input.${showSMSFallback ? "otp-countdown-with-channel" : "new-otp-countdown"}`}
                  isCountingDown={isCountingDown}
                  startCountdown={internalStartCountdown}
                  onStart={handleCountdownStart}
                  onFinish={handleCountdownFinish}
                  otherValues={otherValues}
                  t={t}
                />
              ) : (
                <>
                  <p className="u-mb-2">
                    <Trans t={t} i18nKey={"otp-input.otp-didnt-received"}>
                      <span
                        onClick={handleResendOTP}
                        className="u-text-flip-orange u-cursor-pointer u-font-bold u-underline"
                      />
                    </Trans>
                  </p>
                  <hr className="u-my-6" />
                  <p className="u-mb-2">{t(`otp-input.info-with-other-method.${isOTPViaSMS ? "sms" : "wa"}`)}</p>
                  {hasPhoneNumber ? (
                    <p className="u-mb-2">
                      {showSMSFallback ? (
                        <Trans
                          t={t}
                          i18nKey={`otp-input.send-with-other-method-${
                            isOTPCompanyPhoneNumber ? "company" : "personal"
                          }.${isOTPViaSMS ? "sms" : "wa"}`}
                        >
                          <span
                            onClick={handleSendOTPWithOtherChannel}
                            className="u-text-flip-orange u-cursor-pointer u-font-bold u-underline"
                          />
                          <span
                            onClick={handleSendOTPWithOtherPhone}
                            className="u-text-flip-orange u-cursor-pointer u-font-bold u-underline"
                          />
                        </Trans>
                      ) : (
                        <Trans
                          t={t}
                          i18nKey={`otp-input.non-eligible-whatsapp.${
                            isOTPCompanyPhoneNumber ? "company" : "personal"
                          }`}
                        >
                          <span
                            onClick={handleSendOTPWithOtherPhone}
                            className="u-text-flip-orange u-cursor-pointer u-font-bold u-underline"
                          />
                        </Trans>
                      )}
                    </p>
                  ) : (
                    <p className="u-mb-2">
                      <Trans
                        t={t}
                        i18nKey={
                          showSMSFallback
                            ? `otp-input.send-with-other-method-no-phone-number.${isOTPViaSMS ? "sms" : "wa"}`
                            : "otp-input.non-eligible-whatsapp.company"
                        }
                      >
                        <span
                          onClick={handleSendOTPWithOtherChannel}
                          className="u-text-flip-orange u-cursor-pointer u-font-bold u-underline"
                        />
                      </Trans>
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </Form>
        {isShowAlertActivateGAuth && internalStartCountdown && (
          <div className="u-p-10 u-pt-0 u-text-center">
            <hr className="u-mb-6 u-mt-0" />
            <p className="u-mb-2">{t("otp-input.info-with-other-method.sms")}</p>
            <Alert className="u-text-semi u-text-neutral_main_text u-font-medium" color="grey">
              <Icon icon={faInfoCircle} className="u-mr-2 u-text-neutral_secondary_text" />
              <span>
                <Trans i18nKey={"otp-input.gauth-is-not-setup"} t={t}>
                  <Link
                    className="u-text-flip-orange u-no-underline"
                    to={privateRoutes.settingsTwoFactorAuth.path}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                </Trans>
              </span>
            </Alert>
          </div>
        )}
      </div>
    </ModalStyled>
  );
}

OTPInputModal.defaultProps = defaultProps;

export default OTPInputModal;

// Export the hook for easier usage
export { useOTPModal } from "./useOTPModal";
